import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Send, X } from "lucide-react";
import { validateMessageText } from "../../utils/chatHelpers";
import { color, radius } from "../../styles/theme";

/**
 * Text composer — send new messages or edit an existing one (prefilled when
 * `editingMessage` is set, mirroring mobile's InputToolbar edit mode).
 * Every send/edit is gated through `validateMessageText` first — ported
 * from mobile's chat/index.js `validateMessage`, it blocks emails, links,
 * bare phone numbers, and abusive language (the "stop process for abusive
 * language" mobile has) before the message ever reaches the hub. The
 * blocked text is left in the box so the user can edit and resend, same as
 * mobile.
 * Media/attachment buttons are Phase 2 scope, not built here.
 */
const MessageComposer = ({ onSend, editingMessage, onSubmitEdit, onCancelEdit, disabled, disabledReason }) => {
  const { t } = useTranslation("chat");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (editingMessage) {
      setText(editingMessage.text || "");
      textareaRef.current?.focus();
    }
  }, [editingMessage]);

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    const blockedReasonKey = validateMessageText(trimmed);
    if (blockedReasonKey) {
      toast.warning(t(blockedReasonKey));
      return;
    }

    setSending(true);
    try {
      if (editingMessage) await onSubmitEdit(trimmed);
      else await onSend(trimmed);
      setText("");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape" && editingMessage) onCancelEdit();
  };

  if (disabled) {
    return <div style={s.disabledBanner}>{disabledReason}</div>;
  }

  return (
    <div style={s.wrap}>
      {editingMessage && (
        <div style={s.editingBar}>
          <span style={s.editingLabel}>{t("bubble.edit")}</span>
          <button type="button" style={s.cancelEditBtn} onClick={onCancelEdit}><X size={13} strokeWidth={2} /></button>
        </div>
      )}
      <div style={s.row}>
        <textarea
          ref={textareaRef}
          className="input"
          style={s.textarea}
          rows={1}
          value={text}
          placeholder={editingMessage ? t("composer.editingPlaceholder") : t("composer.placeholder")}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button type="button" className="btn btn-primary" style={s.sendBtn} onClick={handleSubmit} disabled={!text.trim() || sending} aria-label={t("composer.send")}>
          <Send size={15} strokeWidth={2.25} />
        </button>
      </div>
    </div>
  );
};

const s = {
  wrap: { borderTop: `1px solid ${color.border}`, padding: "10px 16px", background: color.surface, flexShrink: 0 },
  editingBar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px 6px" },
  editingLabel: { fontSize: 11, fontWeight: 700, color: color.primary600 },
  cancelEditBtn: { background: "none", border: "none", cursor: "pointer", color: color.textMuted, display: "flex" },
  row: { display: "flex", alignItems: "flex-end", gap: 8 },
  textarea: { flex: 1, resize: "none", maxHeight: 120, minHeight: 38, fontFamily: "inherit" },
  sendBtn: { width: 38, height: 38, padding: 0, borderRadius: radius.sm, flexShrink: 0 },
  disabledBanner: {
    borderTop: `1px solid ${color.border}`, padding: "14px 16px", textAlign: "center",
    fontSize: 12.5, color: color.textMuted, background: color.gray50, flexShrink: 0,
  },
};

export default MessageComposer;
